import React, { useState } from "react"
import PropTypes from "prop-types"
import { Row, Col, Card, Typography, List, Avatar, Flex, Dropdown, Spin, notification } from "antd"
import {
  UserOutlined,
  ShareAltOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  DeleteOutlined,
  FileTextOutlined,
  QuestionCircleOutlined
} from "@ant-design/icons"
import WaiverUploaded from "../../../../../assets/icons/waiver-uploaded.svg"
import WaiverNotSigned from "../../../../../assets/icons/waiver-not-signed.svg"
import Questionnaire from "../../../../../assets/icons/questionnaire.svg"
import styles from "../../index.module.css"
import { GreenCheckIcon, RemoveQuestionnaireIcon } from "icons"
import { WaiverDetailsModal } from "pagesV2/pro/Embedded/BookingConfirmation/modal/WaiverDetails"
import CustomQuestions from "pagesV2/pro/Embedded/BookingConfirmation/modal/CustomQuestions"
import DeleteConfirmation from "pagesV2/pro/DigitalWaiverAndContracts/DocumentsAdded/Deleteconfirmation"
import { useTranslation } from "react-i18next"
import { deleteSharedWaiver, getWaiverSubmissionsByTripBooking, notifyWaiver } from "store/digital-waiver"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import NotifyIcon from "../../../../../assets/icons/new/filters/Notify.svg"
import { NotifyModal } from "../../modal/Notify"
const { Text } = Typography

const MembersCard = React.memo(
  ({
    isSharedWaiverAllowed,
    setTotalDocumentCount,
    setTotalDocuments,
    setPendingWaiverSubmission,
    setIsShowSignForChildWaiver,
    uuid,
    isShowGuestSubmission,
    booking_questionnaire,
    bookingQuestionnaireId,
    guests = [],
    loading,
    onInviteWaiver,
    onSignChildWaiver
  }) => {

    const { t } = useTranslation()
    const dispatch = useDispatch()
    const [showCustomQuestionsModal, setShowCustomQuestionsModal] = useState(false)
    const [isShowWaiverDetailsModal, setIsShowWaiverDetailsModal] = useState(false)
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0)
    const [isShowLoading, setIsShowLoading] = useState(false)
    const [record, setRecord] = useState(null)
    const [questionnaireId, setQuestionnaireId] = useState(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deleteRecord, setDeleteRecord] = useState(null)
    const [questionnaireEditable, setQuestionnaireEditable] = useState(false)
    const [isShowNotifyModal, setIsShowNotifyModal] = useState(false)
    const [currentNotifyGuest, setCurrentNotifyGuest] = useState(null)
    const { loading: deleteLoading } = useSelector((state) => state.digitalWaiver.sharedWaivers)
    const { loading: notifyLoading } = useSelector((state) => state.digitalWaiver.notifyWaiver) || {}

    const handleMenuClick = (e, guest) => {
      switch (e.key) {
        case "open-waiver":
          setIsShowWaiverDetailsModal(true)
          setIsShowLoading(true)
          setRecord(guest)
          // Handle opening waiver
          break
        case "open-questionnaire":
          setShowCustomQuestionsModal(true)
          setQuestionnaireId(guest?.booking_questionnaire?.id || bookingQuestionnaireId)
          setQuestionnaireEditable(guest?.questionnaire_editable)
          break
        case "sign-for-child":
          setIsShowSignForChildWaiver(true)
          setPendingWaiverSubmission(guest?.pending_submission)
          setTotalDocumentCount(guest?.signed_documents ?? 0)
          setTotalDocuments(guest?.total_documents ?? 0)
          break
        case "remove-guest":
          // Handle removing guest
          setShowDeleteModal(true)
          setDeleteRecord(guest)

          break
        case "notify":
          setCurrentNotifyGuest(guest)
          setIsShowNotifyModal(true)
          break
        default:
          break
      }
    }

    const getMenuItems = (guest, index) => {
      const items = []

      if (guest.status === "Signed") {
        items.push({
          key: "open-waiver",
          icon: <FileTextOutlined />,
          label: "Open waiver"
        })
      } else if (guest.participant_type === "Child") {
        items.push({
          key: "sign-for-child",
          icon: <FileTextOutlined />,
          label: "Sign for child"
        })
      }

      const effectiveQuestionnaireId = guest?.booking_questionnaire?.id || (index === 0 ? bookingQuestionnaireId : null)
      if (effectiveQuestionnaireId && index === 0) {
        items.push({
          key: "open-questionnaire",
          icon: guest?.booking_questionnaire?.status === "submitted" ? <EditOutlined /> : <QuestionCircleOutlined />,
          label: guest?.booking_questionnaire?.status === "submitted" ? "Edit Questionnaire" : "Open questionnaire"
        })
      }
      if (guest.shared) {
        items.push(
          {
            type: "divider"
          },
          {
            key: "remove-guest",
            icon: <DeleteOutlined />,
            label: "Remove guest",
            danger: true
          }
        )
      }
      const hasDetails = !!guest?.name
      const isNotSigned = guest?.status !== "Signed"
      const hasNotifyDetails = !!(guest?.notify_details?.email || guest?.notify_details?.phone_number)

      if (hasDetails && isNotSigned && hasNotifyDetails) {
        items.push({
          key: "notify",
          label: "Notify Guest",
          icon: <img src={NotifyIcon} alt="notify" style={{ width: 16, height: 16 }} />
        })
      }

      return items
    }
    const handleDeleteCancel = () => {
      setShowDeleteModal(false)
    }
    const handleDeleteConfirm = () => {
      dispatch(deleteSharedWaiver(deleteRecord.shared_waiver_id)).then((res) => {
        toaster.success(res?.message)
        dispatch(getWaiverSubmissionsByTripBooking(uuid))
        setShowDeleteModal(false)
        setDeleteRecord(null)
      })
    }

    return (
      <>
        <Card className={styles.mainCard} title="Members">
          <Text className="mb-3xl block">
            A few easy steps: sign the waiver, answer a few questions, and you're all set to go.
          </Text>
          <div className={`${styles.sectionTitle} mb-m`}>Required Actions ({(isSharedWaiverAllowed ? 1 : 0) + (!isShowGuestSubmission ? 1 : 0)})</div>
          <div className={`${styles.infoAlert} mb-m`}>
            <span className={styles.infoIcon}>
              <InfoCircleOutlined />
            </span>
            <span>{t("digital_waiver_and_contracts.disclaimer_for_waiver")}</span>
          </div>
          <Row gutter={16} className="mb-xl">
            <Col span={12}>
              <Card className={`${styles.waiverCard} ${!isSharedWaiverAllowed ? styles.disabledCard : "cursor-pointer"}`} onClick={isSharedWaiverAllowed ? onInviteWaiver : undefined}
              style={{opacity: !isSharedWaiverAllowed ? 0.5 : 1, cursor: !isSharedWaiverAllowed ? "not-allowed" : "pointer"}}
              >
                <ShareAltOutlined className={`${styles.waiverCardIcon} text-color-primary mb-xxs`} />
                <div className={`${styles.waiverCardTitle} text-color-primary`}>Invite to sign waiver</div>
                <div className={`${styles.waiverCardRequired} color-text-secondary-dark`}>(required)</div>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                className={`${styles.waiverCard} ${isShowGuestSubmission ? styles.disabledCard : "cursor-pointer"}`}
                onClick={!isShowGuestSubmission ? onSignChildWaiver : undefined}
                style={{
                  opacity: isShowGuestSubmission ? 0.5 : 1,
                  cursor: isShowGuestSubmission ? "not-allowed" : "pointer"
                }}
              >
                <EditOutlined className={`${styles.waiverCardIcon} text-color-primary`} />
                <div className={`${styles.waiverCardTitle} text-color-primary`}>Sign waiver</div>
                <div className={`${styles.waiverCardRequired} color-text-secondary-dark`}>(required)</div>
              </Card>
            </Col>
          </Row>
          <Flex className="mt-3xl mb-m" justify="space-between" align="center" gap={10}>
            <div className={`${styles.sectionTitle}`}>Guests ({guests.length})</div>
          </Flex>
          <Spin spinning={loading} style={{ width: "100%" }}>
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
            <List
              itemLayout="horizontal"
              dataSource={guests}
              renderItem={(guest, index) => {
                let waiverStatusText
                if (guest.total_documents > 0) {
                  waiverStatusText = `${guest.pending_documents > 0 ? (guest.signed_documents ?? 0) : guest.total_documents}/${guest.total_documents} Signed`
                } else {
                  waiverStatusText = guest.status === "Signed" ? "Signed" : "Not signed"
                }
                const effectiveQId = guest?.booking_questionnaire?.id || (index === 0 ? bookingQuestionnaireId : null)
                const questionnaireStatus = guest?.booking_questionnaire?.status || booking_questionnaire?.status
                return (
                <List.Item
                  className={styles.guestItem}
                  actions={[
                    getMenuItems(guest, index).length > 0 ? (
                      <Dropdown
                        key="dropdown"
                        menu={{
                          items: getMenuItems(guest, index),
                          onClick: (e) => handleMenuClick(e, guest)
                        }}
                        trigger={["click"]}
                        placement="bottomRight"
                      >
                        <MoreOutlined className={`${styles.moreIcon} cursor-pointer`} />
                      </Dropdown>
                    ) : (
                      <MoreOutlined key="placeholder" style={{ visibility: "hidden" }} />
                    )
                  ]}
                >
                  <List.Item.Meta
                    className={styles.guestItemMeta}
                    avatar={<Avatar icon={<UserOutlined />} />}
                    title={<span className={`${styles.guestName} fw-500`}>{guest.name}{guest.participant_type === "Primary Guest" ? " (Primary Guest)" : ""}</span>}
                    description={<span className={`${styles.guestType}`}>{guest.participant_type || "Child"}</span>}
                  />
                  <div
                    className={styles.questionnaireStatusBox}
                    style={effectiveQId && index === 0 ? { cursor: "pointer" } : {}}
                    onClick={() => {
                      if (effectiveQId && index === 0) {
                        setShowCustomQuestionsModal(true)
                        setQuestionnaireId(effectiveQId)
                        setQuestionnaireEditable(guest?.questionnaire_editable)
                      }
                    }}
                  >
                    <div
                      className={`${questionnaireStatus === "submitted" ? "Completed" : "Not completed"} commonBox questionnaire`}
                    >
                      {effectiveQId &&
                        index === 0 &&
                        (questionnaireStatus === "submitted" ? (
                          <div className="relative">
                            <img src={Questionnaire} alt="waiver_uploaded" />
                            <GreenCheckIcon className="absolute" />
                          </div>
                        ) : (
                          <div className="relative">
                            <img src={WaiverNotSigned} alt="waiver_not_signed" />
                            <RemoveQuestionnaireIcon className="absolute" />
                          </div>
                        ))}
                      {effectiveQId && index === 0 && (
                        <div>
                          <div className="fw-500">Questionnaire</div>
                          <span>{questionnaireStatus === "submitted" ? "Completed" : "Not completed"}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles.waiverStatusBox}>
                    <div className={`${guest.status === "Signed"} commonBox`}>
                      {guest.status === "Signed" ? (
                        <div className="relative">
                          <img src={WaiverUploaded} alt="waiver_uploaded" />
                          <GreenCheckIcon className="absolute" />
                        </div>
                      ) : (
                        <div className="relative">
                          <img src={WaiverNotSigned} alt="waiver_not_signed" />
                          <RemoveQuestionnaireIcon className="absolute" />
                        </div>
                      )}
                      <div>
                        <div className="fw-500">Waiver</div>
                        <span>{waiverStatusText}</span>
                      </div>
                    </div>
                  </div>
                </List.Item>
                )
              }}
            />
            </div>
          </Spin>
        </Card>
        {showCustomQuestionsModal && (
          <CustomQuestions
            questionnaireEditable={questionnaireEditable}
            uuid={uuid}
            bookingQuestionnaireId={questionnaireId}
            visible={showCustomQuestionsModal}
            onClose={() => setShowCustomQuestionsModal(false)}
          />
        )}

        {isShowWaiverDetailsModal && (
          <WaiverDetailsModal
            uuid={uuid}
            isShowLoading={isShowLoading}
            setIsShowLoading={setIsShowLoading}
            currentDocumentIndex={currentDocumentIndex}
            setCurrentDocumentIndex={setCurrentDocumentIndex}
            record={record}
            open={isShowWaiverDetailsModal}
            onCancel={() => {
              setIsShowWaiverDetailsModal(false)
              setCurrentDocumentIndex(0)
            }}
          />
        )}
        {showDeleteModal && (
          <DeleteConfirmation
            title={"Remove Guest?"}
            // content='Are you sure you want to remove this guest from the trip? This action cannot be undone.'
            content={
              <span>
                {t("digital_waiver_and_contracts.delete_confirmation")} <strong>{deleteRecord?.name}?</strong>{" "}
                {t("digital_waiver_and_contracts.delete_confirmation_suffix")}
              </span>
            }
            visible={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            confirmText="Remove"
            // record={deleteRecord}
            loading={deleteLoading}
          />
        )}
        {isShowNotifyModal && (
          <NotifyModal
            visible={isShowNotifyModal}
            loading={notifyLoading}
            guestData={currentNotifyGuest}
            onClose={() => {
              setIsShowNotifyModal(false)
              setCurrentNotifyGuest(null)
            }}
            onSend={(values) => {
              const payload = {
                shared_waiver_id: currentNotifyGuest?.shared_waiver_id,
                notify_by: values.sendMethod
              }
              if (values.sendMethod === "email") {
                payload.email = values.email
              } else {
                payload.phone_number = values.phoneNumber?.fullNumber
              }
              dispatch(notifyWaiver(uuid, payload)).then((res) => {
                notification.success({
                  message: "Notification Sent",
                  description: res?.message || "Guest has been notified successfully.",
                  placement: "topRight"
                })
                dispatch(getWaiverSubmissionsByTripBooking(uuid))
                setIsShowNotifyModal(false)
                setCurrentNotifyGuest(null)
              })
            }}
          />
        )}
      </>
    )
  }
)

MembersCard.propTypes = {
  tripBookingId: PropTypes.string,
  setTotalDocumentCount: PropTypes.func,
  setTotalDocuments: PropTypes.func,
  visible: PropTypes.bool,
  onclose: PropTypes.func,
  guests: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      waiver: PropTypes.string,
      questionnaire: PropTypes.string,
      main: PropTypes.bool
    })
  ),
  onInviteWaiver: PropTypes.func,
  onSignChildWaiver: PropTypes.func,
  onAddGuest: PropTypes.func,
  setIsShowSignForChildWaiver: PropTypes.func,
  uuid: PropTypes.string,
  isShowGuestSubmission: PropTypes.bool,
  booking_questionnaire: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string
  }),
  bookingQuestionnaireId: PropTypes.number,
  loading: PropTypes.bool
}

MembersCard.displayName = "MembersCard"

export default MembersCard
